/**
 * How long ago a flow sensor last recorded anything.
 *
 * `volumeLastTs` is the timestamp of the newest hourly bucket in the sensor's
 * 24-hour volume ring (Interface and Controller API v204 §3.4.8.6: "tsVolume
 * is the last 24 hours water usage captured by the flow sensor … The
 * tsVolumeLastTs specifies the last entry"). If it stops advancing, the sensor
 * has stopped recording — which nothing else in the product reports, since the
 * rule and valve faults only speak up once irrigation actually runs.
 *
 * THE EPOCH IS NOT UNIX. It is seconds since 2020-01-01. Confirmed by product
 * (2026-08); it is still undocumented in the API spec, so the evidence that
 * originally established it is kept here:
 *
 *  - live payload `volumeLastTs: 208018800` → 2026-08-04 15:00, the day it was
 *    read;
 *  - the SRC simulation fixture pairs `lastTimestamp: 114348959` with
 *    `dateTime: "2023-08-16 11:36:17"`, and this epoch puts it at 11:35:59 —
 *    18 seconds out, i.e. the same moment.
 *
 * The stamp is the CONTROLLER's local time, per the same section ("all in
 * local time") — not the browser's. The two are frequently different: a site
 * is managed from wherever its operator happens to be. So the age is measured
 * against the interface's own `timeZoneOriginId`; comparing against the
 * browser clock instead would report a healthy sensor as hours stale purely
 * because the viewer is in another country.
 */
const CONTROLLER_EPOCH_YEAR = 2020

const HOUR_MS = 60 * 60 * 1000

/**
 * Buckets are hourly and land on the hour, so the newest one is already up to
 * an hour old in normal operation. Three hours allows for that plus a missed
 * bucket before anything is called stale.
 */
export const STALE_AFTER_MS = 3 * HOUR_MS

/** When the sensor last wrote a bucket, or null if it never has. */
export const lastReadingAt = (volumeLastTs?: number | null): Date | null => {
  const seconds = Number(volumeLastTs)
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  const epoch = new Date(CONTROLLER_EPOCH_YEAR, 0, 1).getTime()
  return new Date(epoch + seconds * 1000)
}

/**
 * "Now" as the controller would read it off its own wall clock, expressed on
 * the same local frame `lastReadingAt` returns so the two can be subtracted.
 *
 * Falls back to the browser clock when the interface has no timezone set or
 * names one this engine does not know — a slightly wrong age beats no reading
 * line at all.
 */
const controllerNow = (timeZone: string | null | undefined, now: number) => {
  if (!timeZone) return now
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(now))

    const at = (type: string) =>
      Number(parts.find((part) => part.type === type)?.value)

    return new Date(
      at('year'),
      at('month') - 1,
      at('day'),
      // Some engines render midnight as hour 24 under hour12: false.
      at('hour') % 24,
      at('minute'),
      at('second'),
    ).getTime()
  } catch {
    return now
  }
}

/**
 * How the age is expressed. Kept as a unit and an amount rather than a
 * formatted string so the wording can be translated — this module has no t().
 */
export type ReadingAgeUnit = 'now' | 'min' | 'h' | 'd'

export interface ReadingFreshness {
  /** The age, or null when the sensor has never recorded. */
  age: { amount: number; unit: ReadingAgeUnit } | null
  /** Past STALE_AFTER_MS — worth a look. */
  stale: boolean
}

export const readingFreshness = (
  volumeLastTs?: number | null,
  /** The interface's `timeZoneOriginId` — the controller's own zone. */
  timeZone?: string | null,
  now: number = Date.now(),
): ReadingFreshness => {
  const at = lastReadingAt(volumeLastTs)
  if (!at) return { age: null, stale: false }

  const age = controllerNow(timeZone, now) - at.getTime()
  // A bucket dated in the future means the controller's clock and the
  // browser's disagree; that is not staleness, so it is not reported as such.
  if (age < 0) return { age: { amount: 0, unit: 'now' }, stale: false }

  const minutes = Math.floor(age / 60000)
  const amount =
    minutes < 60
      ? { amount: Math.max(minutes, 1), unit: 'min' as const }
      : age < 48 * HOUR_MS
        ? { amount: Math.floor(age / HOUR_MS), unit: 'h' as const }
        : { amount: Math.floor(age / (24 * HOUR_MS)), unit: 'd' as const }

  return { age: amount, stale: age > STALE_AFTER_MS }
}
